import { AxiosResponse } from 'axios';
import api from 'config/api.config';
import { getErrorMessage } from 'utils/errors';

// ==================== Interfaces ====================

// Admin Users Endpoint
export interface AdminUser {
  _id: string;
  name: string;
  email: string;
  profilePicture: string | null;
  role: string;
  subscription: {
    type: string;
    startDate: string;
    endDate: string;
    status: string;
    autoRenew: boolean;
  };
  payment: {
    lastAmount: number;
    lastPaymentDate: string;
    paymentMethod: string;
    stripeCustomerId: string | null;
    stripeSubscriptionId: string | null;
  };
  accountsCount: number;
  isActive: boolean;
  isEmailVerified: boolean;
  isTwoFactorEnabled: boolean;
  joinDate: string;
  lastLogin: string | null;
}

export interface AdminUsersResponse {
  users: AdminUser[];
  pagination: {
    total: number;
    page: number;
    limit: number;
    totalPages: number;
    hasMore: boolean;
  };
  filters: {
    subscriptionType: string | null;
    status: string | null;
    search: string | null;
    sortBy: string;
    sortOrder: string;
  };
}

export interface AdminUsersQueryParams {
  page?: number;
  limit?: number;
  subscriptionType?: string;
  status?: string;
  search?: string;
  sortBy?: 'created_on' | 'email' | 'subscriptionEndDate';
  sortOrder?: 'asc' | 'desc';
}

// Admin Payments Endpoint
export interface AdminPayment {
  _id: string;
  status: string;
  user: {
    id: string;
    name: string;
    email: string;
  };
  invoiceNumber: string;
  transactionId: string | null;
  amount: number;
  paymentMethod: string;
  paymentDate: string;
  subscriptionType: string;
  subscriptionStartDate: string;
  subscriptionEndDate: string;
  stripeCustomerId: string | null;
  stripeSubscriptionId: string | null;
}

export interface AdminPaymentsResponse {
  payments: AdminPayment[];
  pagination: {
    total: number;
    page: number;
    limit: number;
    totalPages: number;
    hasMore: boolean;
  };
  filters: Record<string, any>;
}

export interface AdminPaymentsQueryParams {
  page?: number;
  limit?: number;
  status?: string;
  subscriptionType?: string;
  paymentMethod?: string;
  search?: string;
  startDate?: string;
  endDate?: string;
  minAmount?: number;
  maxAmount?: number;
  sortBy?: 'payment_date' | 'amount' | 'created_on';
  sortOrder?: 'asc' | 'desc';
}

// ==================== API Functions ====================

/**
 * Get admin users list
 */
export const getAdminUsers = async (params?: AdminUsersQueryParams): Promise<AdminUsersResponse> => {
  try {
    const queryParams: Record<string, unknown> = {
      page: params?.page || 1,
      limit: params?.limit || 10,
    };

    if (params?.subscriptionType) queryParams.subscriptionType = params.subscriptionType;
    if (params?.status) queryParams.status = params.status;
    if (params?.search) queryParams.search = params.search;
    if (params?.sortBy) queryParams.sortBy = params.sortBy;
    if (params?.sortOrder) queryParams.sortOrder = params.sortOrder;

    const response: AxiosResponse<AdminUsersResponse> = await api.get(
      `/api/admin/users`,
      { params: queryParams }
    );
    return response.data;
  } catch (error: unknown) {
    throw new Error(getErrorMessage(error, 'Failed to fetch admin users'));
  }
};

/**
 * Get admin payments list
 */
export const getAdminPayments = async (params?: AdminPaymentsQueryParams): Promise<AdminPaymentsResponse> => {
  try {
    const queryParams: Record<string, unknown> = {
      page: params?.page || 1,
      limit: params?.limit || 10,
    };

    if (params?.status) queryParams.status = params.status;
    if (params?.subscriptionType) queryParams.subscriptionType = params.subscriptionType;
    if (params?.paymentMethod) queryParams.paymentMethod = params.paymentMethod;
    if (params?.search) queryParams.search = params.search;
    if (params?.startDate) queryParams.startDate = params.startDate;
    if (params?.endDate) queryParams.endDate = params.endDate;
    if (params?.minAmount !== undefined) queryParams.minAmount = params.minAmount;
    if (params?.maxAmount !== undefined) queryParams.maxAmount = params.maxAmount;
    if (params?.sortBy) queryParams.sortBy = params.sortBy;
    if (params?.sortOrder) queryParams.sortOrder = params.sortOrder;

    const response: AxiosResponse<AdminPaymentsResponse> = await api.get(
      `/api/admin/payments`,
      { params: queryParams }
    );
    return response.data;
  } catch (error: unknown) {
    throw new Error(getErrorMessage(error, 'Failed to fetch admin payments'));
  }
};
